import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
interface NumberLineProps {
  start: number;
  end: number;
  highlight?: number[];
  marked?: number[];
  step?: number;
  label?: string;
  mode?: 'stimulus' | 'support';
  /** Optional custom labels for each tick mark (overrides auto-generated labels) */
  tickLabels?: string[];
  /** Optional map from decimal value to display label (e.g., { "0.25": "¼", "0.5": "½" }) */
  fractionLabels?: Record<string, string>;
}
function isFiniteNumber(n: unknown): n is number {
  return typeof n === 'number' && Number.isFinite(n);
}
function formatTick(value: number, maxFractionDigits: number) {
  const nf = new Intl.NumberFormat(undefined, {
    maximumFractionDigits: maxFractionDigits,
    minimumFractionDigits: 0,
    notation: 'standard'
  });
  return nf.format(value);
}
function approxIncludes(values: number[], target: number, epsilon: number) {
  for (const v of values) {
    if (Math.abs(v - target) <= epsilon) return true;
  }
  return false;
}
/**
 * Look up a display label for a tick value.
 * Checks fractionLabels map with approximate matching, falls back to formatTick.
 */
function getTickLabel(value: number, maxFractionDigits: number, tickLabels: string[] | undefined, tickIndex: number, fractionLabels: Record<string, string> | undefined): string {
  // 1. If explicit tickLabels array is provided, use it
  if (tickLabels && tickIndex < tickLabels.length) {
    return tickLabels[tickIndex];
  }
  // 2. If fractionLabels map is provided, look up by approximate value
  if (fractionLabels) {
    for (const [key, label] of Object.entries(fractionLabels)) {
      const keyNum = parseFloat(key);
      if (!isNaN(keyNum) && Math.abs(keyNum - value) < 0.001) {
        return label;
      }
    }
  }
  // 3. Default: use numeric format
  return formatTick(value, maxFractionDigits);
}
/**
 * Enhanced NumberLine with didactic place value support
 * - Clean, professional UI
 * - Exact positioning
 * - Educational explanations
 */
export function NumberLine({
  start,
  end,
  highlight = [],
  marked,
  step,
  label,
  mode = 'support',
  tickLabels,
  fractionLabels
}: NumberLineProps) {
  // Merge marked (from seed data) into highlight
  const effectiveHighlight = marked && marked.length > 0 ? [...highlight, ...marked] : highlight;
  const computed = useMemo(() => {
    const validInputs = isFiniteNumber(start) && isFiniteNumber(end);
    const min = validInputs ? Math.min(start, end) : 0;
    const max = validInputs ? Math.max(start, end) : 0;
    const range = max - min;
    // Calculate steps: use explicit step prop if provided, otherwise auto-detect
    let steps: number;
    let stepSize: number;
    if (isFiniteNumber(step) && step > 0 && range > 0) {
      // Use explicit step from seed data
      steps = Math.round(range / step);
      stepSize = step;
    } else if (range > 0) {
      // Auto-detect: prefer integer steps for integer ranges
      const isIntegerRange = Number.isInteger(min) && Number.isInteger(max);
      if (isIntegerRange && range <= 30) {
        // Use 1-unit steps for small integer ranges
        steps = range;
        stepSize = 1;
      } else if (isIntegerRange && range <= 100) {
        // Use steps that divide evenly
        const candidates = [1, 2, 5, 10, 20, 25, 50];
        const bestStep = candidates.find(c => range / c <= 20 && range % c === 0) || Math.ceil(range / 10);
        steps = Math.round(range / bestStep);
        stepSize = bestStep;
      } else {
        // Fallback to 10 steps for non-integer or very large ranges
        steps = 10;
        stepSize = range / steps;
      }
    } else {
      steps = 10;
      stepSize = range === 0 ? 1 : range / steps;
    }
    const maxFractionDigits = (() => {
      if (stepSize >= 1) return 0;
      if (stepSize >= 0.1) return 1;
      if (stepSize >= 0.01) return 2;
      if (stepSize >= 0.001) return 3;
      return 6;
    })();
    const epsilon = Math.max(stepSize / 2, Math.pow(10, -maxFractionDigits) / 2);
    const ticks = validInputs && range !== 0 ? Array.from({
      length: steps + 1
    }, (_, i) => min + i * stepSize) : [];
    return {
      validInputs,
      min,
      max,
      range,
      steps,
      stepSize,
      maxFractionDigits,
      epsilon,
      ticks
    };
  }, [start, end, step]);
  const cleanedHighlights = useMemo(() => effectiveHighlight.filter(isFiniteNumber), [effectiveHighlight]);
  if (!computed.validInputs) return null;
  if (computed.range === 0) {
    return <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-2xl border-2 border-blue-200" data-id="element-7003">
        {label && <div className="text-sm font-bold text-blue-900 mb-4 text-center" data-id="element-7004">
            {label}
          </div>}
        <div className="text-center text-slate-700 text-2xl font-bold" data-id="element-7005">
          {formatTick(computed.min, 6)}
        </div>
      </div>;
  }
  // Helper to get display label for a tick
  const labelForTick = (value: number, index: number) => getTickLabel(value, computed.maxFractionDigits, tickLabels, index, fractionLabels);
  // ── Shared: number line visualization (used in both modes) ────────────────
  const numberLineViz = <div className="relative h-28" data-id="element-7006">
      <div className="absolute top-1/2 left-0 right-0 h-2.5 bg-gradient-to-r from-blue-400 via-indigo-500 to-blue-400 rounded-full shadow-md -translate-y-1/2" data-id="element-7007" />
      <div className="absolute top-1/2 left-0 -translate-y-1/2 -translate-x-1/2" data-id="element-7008">
        <div className="w-5 h-5 rounded-full bg-blue-600 border-2 border-white shadow-lg" data-id="element-7009" />
        <div className="absolute top-7 left-1/2 -translate-x-1/2 text-xs font-bold text-blue-900 bg-white/90 px-2 py-0.5 rounded-lg border border-blue-200 shadow-sm whitespace-nowrap" data-id="element-7010">
          {labelForTick(computed.min, 0)}
        </div>
      </div>
      <div className="absolute top-1/2 right-0 -translate-y-1/2 translate-x-1/2" data-id="element-7011">
        <div className="w-5 h-5 rounded-full bg-blue-600 border-2 border-white shadow-lg" data-id="element-7012" />
        <div className="absolute top-7 left-1/2 -translate-x-1/2 text-xs font-bold text-blue-900 bg-white/90 px-2 py-0.5 rounded-lg border border-blue-200 shadow-sm whitespace-nowrap" data-id="element-7013">
          {labelForTick(computed.max, computed.steps)}
        </div>
      </div>
      {computed.ticks.map((value, i) => {
      const position = i / computed.steps * 100;
      const isHighlighted = approxIncludes(cleanedHighlights, value, computed.epsilon);
      const tickLabel = labelForTick(value, i);
      // Skip non-highlighted first/last tick labels — endpoint dots already show them
      const isEndpoint = i === 0 || i === computed.steps;
      const hideTickLabel = isEndpoint && !isHighlighted;
      // In stimulus mode, hide values for highlighted dots (don't spoil the answer)
      const isStimulusHighlight = mode === 'stimulus' && isHighlighted;
      return <motion.div key={i} initial={{
        scale: 0,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} transition={{
        delay: i * 0.04,
        type: 'spring',
        stiffness: 300,
        damping: 20
      }} className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2" style={{
        left: `${position}%`
      }} data-id="element-7014">
            {/* Hide endpoint dots when not highlighted (the outer endpoint dots handle them) */}
            {!(isEndpoint && !isHighlighted) && <div className={`rounded-full relative z-10 mx-auto
              ${isHighlighted ? 'w-6 h-6 bg-indigo-600 ring-4 ring-indigo-200 shadow-xl' : 'w-3 h-3 bg-blue-400 shadow-md'}`} data-id="element-7015" />}
            {/* Tick label: hide for endpoints handled by outer dots, and hide value for stimulus highlights */}
            {!hideTickLabel && !isStimulusHighlight && <div className={`absolute top-9 left-1/2 -translate-x-1/2 text-xs font-bold whitespace-nowrap
              ${isHighlighted ? 'text-indigo-800' : 'text-slate-500'}`} data-id="element-7016">
                {tickLabel}
              </div>}
            {/* Tooltip: in stimulus mode show "?" marker, in support mode show value */}
            {isHighlighted && <motion.div initial={{
          opacity: 0,
          y: -8
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3
        }} className={`absolute -top-12 left-1/2 -translate-x-1/2 ${isStimulusHighlight ? 'bg-indigo-500' : 'bg-indigo-600'} text-white px-3 py-1.5 rounded-xl text-sm font-bold shadow-xl whitespace-nowrap z-20`} data-id="element-7017">
                {isStimulusHighlight ? '?' : tickLabel}
                <div className={`absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[5px] border-r-[5px] border-t-[5px] border-transparent ${isStimulusHighlight ? 'border-t-indigo-500' : 'border-t-indigo-600'}`} data-id="element-7018" />
              </motion.div>}
          </motion.div>;
    })}
    </div>;
  // ── STIMULUS MODE: visualization only ─────────────────────────────────────
  if (mode === 'stimulus') {
    return <div className="bg-blue-50 rounded-xl px-6 pt-4 pb-8 border border-blue-200" data-id="element-7019">
        {label && <p className="text-sm font-semibold text-blue-800 mb-3 text-center" data-id="element-7020">
            {label}
          </p>}
        {numberLineViz}
      </div>;
  }
  // ── SUPPORT MODE: full educational panel ──────────────────────────────────
  return <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-8 rounded-2xl border-2 border-blue-200 shadow-sm" data-id="element-7021">
      {label && <div className="mb-8 text-center" data-id="element-7022">
          <div className="inline-flex items-center gap-3 bg-white/90 rounded-xl px-5 py-3 border border-blue-200 shadow-sm" data-id="element-7023">
            <div className="w-2.5 h-2.5 rounded-full bg-indigo-600 animate-pulse" data-id="element-7024" />
            <span className="text-base font-bold text-blue-900" data-id="element-7025">{label}</span>
          </div>
        </div>}

      <div className="mb-8" data-id="element-7026">{numberLineViz}</div>

      <div className="space-y-4" data-id="element-7027">
        <div className="bg-white/80 rounded-xl p-5 border border-blue-100 shadow-sm" data-id="element-7028">
          <div className="flex items-start gap-3" data-id="element-7029">
            <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0" data-id="element-7030">
              <span className="text-indigo-600 text-lg" data-id="element-7031">📊</span>
            </div>
            <div data-id="element-7032">
              <h4 className="text-sm font-bold text-indigo-900 mb-2" data-id="element-7033">
                Wat zie je hier?
              </h4>
              <p className="text-sm text-slate-700 leading-relaxed" data-id="element-7034">
                Deze getallenlijn loopt van{' '}
                <strong data-id="element-7035">{labelForTick(computed.min, 0)}</strong> tot{' '}
                <strong data-id="element-7036">{labelForTick(computed.max, computed.steps)}</strong>.
                Elk streepje is{' '}
                <strong data-id="element-7037">
                  {formatTick(computed.stepSize, computed.maxFractionDigits)}
                </strong>{' '}
                verder.
                {cleanedHighlights.length > 0 && <span className="block mt-2 text-indigo-700 font-semibold" data-id="element-7038">
                    De blauwe punt{cleanedHighlights.length > 1 ? 'en' : ''}{' '}
                    laat zien waar{' '}
                    {cleanedHighlights.map((h, i) => {
                  // Find the closest tick index for this highlight
                  const closestIdx = computed.ticks.reduce((bestIdx, tick, idx) => Math.abs(tick - h) < Math.abs(computed.ticks[bestIdx] - h) ? idx : bestIdx, 0);
                  return <span key={i} data-id="element-7039">
                          <strong data-id="element-7040">{labelForTick(h, closestIdx)}</strong>
                          {i < cleanedHighlights.length - 1 && (i === cleanedHighlights.length - 2 ? ' en ' : ', ')}
                        </span>;
                })}{' '}
                    {cleanedHighlights.length > 1 ? 'liggen' : 'ligt'}.
                  </span>}
              </p>
            </div>
          </div>
        </div>

        {cleanedHighlights.length > 0 && <div className="bg-emerald-50/80 rounded-xl p-5 border border-emerald-200 shadow-sm" data-id="element-7041">
            <div className="flex items-start gap-3" data-id="element-7042">
              <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0" data-id="element-7043">
                <span className="text-emerald-600 text-lg" data-id="element-7044">💡</span>
              </div>
              <div data-id="element-7045">
                <h4 className="text-sm font-bold text-emerald-900 mb-2" data-id="element-7046">
                  Waarom deze waarde?
                </h4>
                <p className="text-sm text-slate-700 leading-relaxed" data-id="element-7047">
                  {cleanedHighlights.map((h, i) => {
                const stepsFromStart = Math.round((h - computed.min) / computed.stepSize);
                const closestIdx = computed.ticks.reduce((bestIdx, tick, idx) => Math.abs(tick - h) < Math.abs(computed.ticks[bestIdx] - h) ? idx : bestIdx, 0);
                return <span key={i} className="block mb-2 last:mb-0" data-id="element-7048">
                        <strong className="text-emerald-700" data-id="element-7049">
                          {labelForTick(h, closestIdx)}
                        </strong>{' '}
                        ligt{' '}
                        <strong data-id="element-7050">
                          {stepsFromStart}{' '}
                          {stepsFromStart === 1 ? 'stap' : 'stappen'}
                        </strong>{' '}
                        vanaf het begin.
                        {stepsFromStart > 0 && <span className="text-slate-600" data-id="element-7051">
                            {' '}
                            ({labelForTick(computed.min, 0)} + {stepsFromStart}{' '}
                            ×{' '}
                            {formatTick(computed.stepSize, computed.maxFractionDigits)}{' '}
                            = {labelForTick(h, closestIdx)})
                          </span>}
                      </span>;
              })}
                </p>
              </div>
            </div>
          </div>}

        <div className="bg-amber-50/80 rounded-xl p-5 border border-amber-200 shadow-sm" data-id="element-7052">
          <div className="flex items-start gap-3" data-id="element-7053">
            <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0" data-id="element-7054">
              <span className="text-amber-600 text-lg" data-id="element-7055">🎯</span>
            </div>
            <div data-id="element-7056">
              <h4 className="text-sm font-bold text-amber-900 mb-2" data-id="element-7057">
                Positiewaarde begrijpen
              </h4>
              <p className="text-sm text-slate-700 leading-relaxed" data-id="element-7058">
                Elk cijfer heeft een <strong data-id="element-7059">positiewaarde</strong>. Hoe verder
                naar links, hoe groter de waarde.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>;
}